import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/authContext.tsx";
import { isTopGlobalEndpoint } from "../../auth/endpointUtils.ts";
import { getPagePath } from "../../pages/pageRegistry.ts";
import { superAdminNavEntries, type SuperAdminNavEntry } from "./superAdminNavRegistry.ts";
import styles from "./SuperAdminNavBadge.module.scss";

interface Props {
  /** Called after a nav entry is picked, so the parent can dismiss the SA panel. */
  onPick?: () => void;
}

/**
 * Yellow [SA] badge that sits inside the SuperAdmin panel header. Click opens a
 * popover listing the SuperAdmin-only nav entries (Page Management, galleries,
 * future debug pages). Selecting one navigates and dismisses the panel.
 */
export function SuperAdminNavBadge({ onPick }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const onGlobal = isTopGlobalEndpoint(useAuth().activeEndpoint);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const grouped = useMemo(() => {
    const map = new Map<string, SuperAdminNavEntry[]>();
    for (const e of superAdminNavEntries) {
      if (e.globalOnly && !onGlobal) continue;
      const arr = map.get(e.group) ?? [];
      arr.push(e);
      map.set(e.group, arr);
    }
    return Array.from(map.entries());
  }, [onGlobal]);

  function pick(entry: SuperAdminNavEntry) {
    setOpen(false);
    onPick?.();
    navigate(getPagePath(entry.pageName));
  }

  return (
    <div ref={rootRef} className={styles.root}>
      <button
        type="button"
        className={open ? styles.badgeOpen : styles.badge}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="SuperAdmin navigation"
        title="SuperAdmin navigation"
      >
        SA
      </button>
      {open && (
        <div className={styles.popover} role="menu">
          {grouped.map(([group, entries]) => (
            <div key={group}>
              <div className={styles.group}>{group}</div>
              {entries.map((entry) => (
                <button
                  key={entry.pageName}
                  type="button"
                  className={entry.indent ? styles.entryIndent : styles.entry}
                  onClick={() => pick(entry)}
                  role="menuitem"
                >
                  {entry.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
